import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComboboxStore } from '@mantine/core';
import { useDebouncedCallback } from '@mantine/hooks';
import type {
  Suggestion,
  SuggestionsByType,
  SuggestionTypes,
} from '@/features/searchSlice';

const MIN_SEARCH_TERM_LENGTH = 3;
export const DEBOUNCE_MS = 500;
/** Soft cap on cached terms; oldest entries are evicted FIFO past this size. */
const CACHE_MAX_ENTRIES = 50;

interface GroupedOption {
  readonly label: SuggestionTypes;
  readonly options: readonly Suggestion[];
}

interface UseSearchEngineArgs {
  /** Called with the typed term to fetch grouped suggestions. */
  search: (term: string) => Promise<SuggestionsByType>;
  /** Mantine combobox store; used to open/close the dropdown imperatively. */
  combobox: ComboboxStore;
}

const isQueryable = (term: string) => term.length >= MIN_SEARCH_TERM_LENGTH;

/** Flattens the API's `SuggestionsByType` map into ordered group entries. */
const transformSuggestions = (
  suggestions?: SuggestionsByType
): GroupedOption[] =>
  Object.entries(suggestions ?? {}).map(([key, values]) => ({
    label: key as SuggestionTypes,
    options: values ?? [],
  }));

/**
 * Owns the SearchBar's input → debounce → cache → fetch → results state machine.
 *
 * Returns the surface needed by the UI shell. All timer / cache / sequence
 * bookkeeping is internal. Reset on external state changes is handled by the
 * caller via a `key` remount, not by this hook.
 */
export const useSearchEngine = ({ search, combobox }: UseSearchEngineArgs) => {
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState<GroupedOption[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  /** Search term → grouped-results cache.*/
  const cacheRef = useRef<Map<string, GroupedOption[]>>(new Map());
  /**
   * The term the user currently wants results for. Updated on every keystroke;
   * any in-flight `runSearch` whose `term` no longer matches drops its response,
   * so a slow result for "a" can't clobber the already-rendered results for "ab".
   */
  const latestTermRef = useRef('');

  /**
   * Latest-value refs for inputs that change identity between renders. The
   * Mantine combobox store returns a fresh wrapper each render but dispatches
   * to a stable internal store; `search` rebuilds whenever the caller's
   * dependencies (e.g. `mapCenter`) change. Mirroring both into refs lets
   * `runSearch` stay stable so `useDebouncedCallback` keeps a single timer
   * across re-renders.
   */
  const comboboxRef = useRef(combobox);
  const searchRef = useRef(search);
  useEffect(() => {
    comboboxRef.current = combobox;
  }, [combobox]);
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  const runSearch = useCallback(async (term: string) => {
    if (!isQueryable(term)) {
      setResults([]);
      setIsFetching(false);
      return;
    }

    // A debounced call can fire after the user has cleared the input — the
    // empty branch in `onChange` doesn't reschedule, so the prior timer
    // survives. Bail before flipping the loader on; otherwise nothing later
    // will turn it back off.
    if (latestTermRef.current !== term) return;

    const cached = cacheRef.current.get(term);
    if (cached) {
      setResults(cached);
      setIsFetching(false);
      comboboxRef.current.openDropdown();
      return;
    }

    // Go get a new result. Show the loader and proceed.
    setIsFetching(true);
    const raw = await searchRef.current(term);

    // Drop stale responses — the user has typed something else since.
    if (latestTermRef.current !== term) {
      // If the user cleared the input mid-flight, no later runSearch will
      // fire to flip the loader off — own the reset here.
      if (!isQueryable(latestTermRef.current)) setIsFetching(false);
      return;
    }

    const transformed = transformSuggestions(raw);

    // Evict the oldest entry to keep the cache size in check.
    if (cacheRef.current.size >= CACHE_MAX_ENTRIES) {
      const oldest = cacheRef.current.keys().next().value;
      if (oldest !== undefined) cacheRef.current.delete(oldest);
    }

    cacheRef.current.set(term, transformed);
    setResults(transformed);
    setIsFetching(false);
    comboboxRef.current.openDropdown();
  }, []);

  const debouncedRunSearch = useDebouncedCallback(runSearch, DEBOUNCE_MS);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.currentTarget.value;
      setInputValue(next);
      latestTermRef.current = next;
      if (!isQueryable(next)) {
        setResults([]);
        setIsFetching(false);
        comboboxRef.current.closeDropdown();
        return;
      }
      debouncedRunSearch(next);
    },
    [debouncedRunSearch]
  );

  /**
   * Flat lookup from option value string → suggestion. Keyed by
   * `${group}:${suggestion.value}` so identical display values across groups
   * (e.g. a coordinate string that matches an address label) don't collide —
   * Mantine requires every `Combobox.Option value` to be unique.
   */
  const optionMap = useMemo(
    () =>
      new Map<string, Suggestion>(
        results.flatMap((g) =>
          g.options.map((s) => [`${g.label}:${s.value}`, s] as const)
        )
      ),
    [results]
  );

  /**
   * Resolves the suggestion behind an option value (the string Mantine's
   * `Combobox.onOptionSubmit` hands back). Returns `undefined` for unknown
   * values; the caller decides what to do with link vs callback kinds.
   */
  const getSuggestion = (value: string): Suggestion | undefined =>
    optionMap.get(value);

  return { inputValue, onChange, results, isFetching, getSuggestion };
};
